//MapTips.tsx =================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as ElevationProfile from "../Widgets/ElevationProfile";
import * as Highlight from "../Util/Highlight";
import * as MainMap from "../MainMap";
import * as MapTips_Custom from "../Util/MapTips_Custom";
import * as SelectOnMap from "../Search/SelectOnMap";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as Widgets from "../Widgets/Widgets";

import { CssBaseline, Paper, Zoom } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

import React from "react";
import ReactDOM from "react-dom/client";

//Map tips are disabled for these tools.
const mapTipDisabledTools: string[] = ["Agricultural Use", "Compare Maps", "Measure", "Print", "Sales Search", "Select on Map", "Sketch"];

let mapTipDiv: HTMLDivElement;
let tipVisible: boolean = false;
let mouseOver: boolean = false;
let xPrev: number = -1;
let yPrev: number = -1;

let mapTipSetting: Settings.iMapTip;
let mapTipFields: Util_App.iField[];

let top: number;
let bottom: number;
let left: number;
let right: number;

let radBL: number;
let radBR: number;
let radTL: number;
let radTR: number;
let root: ReactDOM.Root;

//Initialize map tips.
export function initMapTips() {
   Util.logMsg("Initializing Map Tips.");
   let caseField: string;

   //Initialize menu.
   if (localStorage.Exists("ShowMapTips")) {
      Settings.setShowMapTips(localStorage.GetItem("ShowMapTips") as boolean);
   }

   //Initialize settings.
   for (const mapTipLayer of Settings.mapTipLayers) {

      //Set default scales.
      if (!mapTipLayer.maxScale) {
         mapTipLayer.maxScale = 0;
      }

      if (!mapTipLayer.minScale) {
         mapTipLayer.minScale = 999999999;
      }

      //Fix case of map tip field names.
      const featureLayer = Util_App.getOperationalLayerById(mapTipLayer.layerName) as __esri.FeatureLayer;

      if (featureLayer) {
         const outFields: string[] = [];

         for (const fieldName of mapTipLayer.fieldNames) {
            caseField = Util_App.getFieldNameCase(fieldName, featureLayer.fields);
            if (caseField) {
               outFields.Add(caseField);
            } else {
               Util.errMsg("Field name not found for map tip setting: " + fieldName);
            }
         }

         if (outFields.length === 0) {
            Util.errMsg("No valid field names for map tip layer: " + mapTipLayer.layerName);
            continue; //No fields found that match settings.
         }
         mapTipLayer.fieldNames = outFields;
      }
   }

   //Get map tip div element.
   mapTipDiv = document.getElementById("mapTip_div") as HTMLDivElement;

   //Mouse move event.
   MainMap.activeView.on("pointer-move", function (event: __esri.ViewPointerMoveEvent) {
      if (Settings.showMapTips) {
         if (!SelectOnMap.active && !ElevationProfile?.elevationProfileWidget?.visible && !mapTipDisabledTools.Contains(DrawerMenu.curToolName)) {
            const buttons = event.native.buttons as number;
            const shiftKey = event.native.shiftKey as boolean;

            if (!shiftKey && buttons === 0 && Highlight.stationary) { //Skip pan and zoom operations.
               if (!ElevationProfile?.elevationProfileWidget?.visible && MainMap.activeView.ready) {
                  setTimeout(function () { procHitTest(event); }, 600);
               }
            }
            xPrev = event.x;
            yPrev = event.y;
         }
      }
   });

   //Mouse leave event.
   MainMap.activeView.on("pointer-leave", function () {
      xPrev = -1;
      setTimeout(function () { hide(); }, 200);
   });

   //Hide map tips when panning or zooming.
   reactiveUtils.when(() => MainMap.activeView?.interacting === true, function () {
      hide();
   });
}

//Process hit test.
function procHitTest(event: __esri.ViewPointerMoveEvent) {
   if (event.x === xPrev && event.y === yPrev) {
      //Util.logMsg("xy match: " + xPrev + " " + yPrev);
      processHitTest(event);
   } else {
      hide();
   }
}

//Show map tip.
function show() {
   if (!mouseOver) {
      //Util.logMsg("Showing tip: " + mapTipSetting.layerName);
      tipVisible = true;
      if (!root) {
         root = ReactDOM.createRoot(mapTipDiv);
      }
      root.render(<Render />);
   }
}

//Hide map tip.
function hide() {
   if (!mouseOver && tipVisible) {
      //Util.logMsg("Hiding tip");
      tipVisible = false;
      if (!root) {
         root = ReactDOM.createRoot(mapTipDiv);
      }
      root.render(<Render />);
   }
}

//Process all feature layers.
function processHitTest(event: __esri.ViewPointerMoveEvent) {

   MainMap.activeView.hitTest(event).then(function (response: __esri.HitTestResult) {
      let found: boolean = false;

      //Process all map tip layer settings.
      for (const mapTipLayer of Settings.mapTipLayers) {

         if (MainMap.activeView.scale > mapTipLayer.minScale || MainMap.activeView.scale < mapTipLayer.maxScale) {
            continue; //Skip if not within scale.
         }

         //Process all graphic layers.
         for (let result of response.results) {
            result = result as __esri.GraphicHit;

            const featureLayer = result.graphic.layer as __esri.FeatureLayer;

            //Process the feature layer.
            if (featureLayer) {
               const layerName: string = featureLayer.id;
               if (layerName === mapTipLayer.layerName) {

                  //Get object id.
                  let objectId = 0;
                  if (result.graphic.attributes && featureLayer) {
                     if (featureLayer.objectIdField) {
                        objectId = result.graphic.attributes[featureLayer.objectIdField] as number;
                     }
                  }

                  found = true;
                  featureLayer.outFields = mapTipLayer.fieldNames;

                  MainMap.activeView.whenLayerView(featureLayer).then(function (layerView: __esri.FeatureLayerView) { //Layer view is ready.
                     reactiveUtils.whenOnce(() => !layerView.updating).then(function () { //Layer view is done updating.

                        let query = layerView.createQuery();
                        query.outFields = mapTipLayer.fieldNames;
                        query.objectIds = [objectId];

                        layerView.queryFeatures(query).then(function (fs) {
                           if (fs.features.length > 0) {

                              for (const tipSetting of Settings.mapTipLayers) {
                                 if (tipSetting.layerName === layerName) {
                                    setPosition(event.x, event.y);
                                    mapTipSetting = tipSetting;
                                    mapTipFields = Util_App.getFeatureValues(fs, fs.features[0], layerName);
                                    show();
                                    break;
                                 }
                              }
                           }

                        }).catch(function (error: Error) {
                           Util.errMsg("MapTips: layerView.queryFeatures failed: ", error);
                        }); // end layerView.queryFeatures.
                     }); // end updating is complete.

                  }); // end when layer view ready.
                  break;
               }
            }
         } //end for all graphic results.

         if (found) {
            break;
         }
      } //end for all map tip layers.
   }); //end hit test.
}

//Set screen position.
function setPosition(x: number, y: number) {
   const width: number = Util.windowWidth();
   const height: number = Util.windowHeight();
   const xMargin: number = width - x;
   const yMargin: number = height - y;
   const borderRadius: number = 7;
   const offset: number = 20;

   top = null;
   bottom = null;
   left = null;
   right = null;

   if (y < (height / 2)) {
      top = y + offset;
   } else {
      bottom = yMargin + offset;
   }

   if (xMargin < (width / 2)) {
      right = xMargin + offset;
   } else {
      left = x + offset;
   }

   radTL = borderRadius;
   radTR = borderRadius;
   radBL = borderRadius;
   radBR = borderRadius;

   if (top && right) {
      radTR = 0;
   } else if (top & left) {
      radTL = 0;
   } else if (bottom && right) {
      radBR = 0;
   } else {
      radBL = 0;
   }
}

//Mouse over map tip.
function mouseOverOut(over: boolean) {
   //Util.logMsg("Mouse over");
   mouseOver = over;
}

//Render function.
export function Render() {
   //Util.logMsg("Rendering: " + mapTipSetting.layerName);

   //css styles.
   const styles: Default.iStyles = {
      paper: { position: 'absolute', border: '1px solid DarkSlateGray', padding: 7, paddingTop: 3, paddingBottom: 2, top: top, bottom: bottom, left: left, right: right, zIndex: 4, borderBottomLeftRadius: radBL, borderTopLeftRadius: radTL, borderTopRightRadius: radTR, borderBottomRightRadius: radBR, cursor: 'default' },
   };

   return (
      <ThemeProvider theme={Default.theme}>
         <CssBaseline />

         <Zoom in={tipVisible} timeout={400} mountOnEnter unmountOnExit>
            <Paper style={styles.paper} onMouseOver={() => mouseOverOut(true)} onMouseOut={() => mouseOverOut(false)}>
               {MapTips_Custom.getMapTip(mapTipSetting, mapTipFields)}
            </Paper>
         </Zoom>

      </ThemeProvider>
   ); //end return.

} //end function.
